import { FunctionComponent, useState } from "react";
import { UserModel } from "../../../../../context_providers/auth/user_model";
import "../../styles/visiting-card.css";
import HrmLoader from "../../components/loading";

interface VisitingCardProps {
  user: UserModel;
}

const VisitingCard: FunctionComponent<VisitingCardProps> = ({ user }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const generateNewCanvasIdCard = async (): Promise<void> => {
    try {
      setLoading(true);

      await generateSingleCanvasCard("front");
      await new Promise((resolve) => setTimeout(resolve, 500));
      await generateSingleCanvasCard("back");
    } catch (error) {
      console.error("Error generating canvas cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateSingleCanvasCard = async (
    side: "front" | "back"
  ): Promise<void> => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) throw new Error("Canvas context not available");

    const cardWidth = 520;
    const cardHeight = 285;

    canvas.width = cardWidth * 2;
    canvas.height = cardHeight * 2;
    ctx.scale(2, 2);

    if (side === "front") {
      await drawVisitingCardFront(ctx, cardWidth, cardHeight);
    } else {
      await drawVisitingCardBack(ctx, cardWidth, cardHeight);
    }

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = `${
            user?.first_name || "employee"
          }_new_id_card_${side}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        }
      },
      "image/png",
      1.0
    );
  };

  const drawVisitingCardFront = async (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): Promise<void> => {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    try {
      const logoImg = await loadImageFromUrl("/images/id-card/kazamLogo.png");
      const logoWidth = 120;
      const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
      ctx.drawImage(logoImg, 30, 20, logoWidth, logoHeight);
    } catch (err) {
      console.warn("Could not load Kazam logo:", err);
    }

    ctx.font = "20px Arial";
    ctx.textAlign = "left";
    ctx.fillStyle = "#565656";
    ctx.fillText("Single platform for EV charging", 30, 119);

    try {
      const qrImg = await loadImageFromUrl("/images/id-card/web_url.png");
      const qrSize = 50;
      const qrX = 30;
      const qrY = height - qrSize - 40;
      ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

      ctx.fillStyle = "#5b5c5c";
      ctx.font = "12px Arial";
      ctx.textAlign = "left";
      ctx.fillText("www.kazam.in", qrX, qrY + qrSize + 18);
    } catch (err) {
      console.warn("Could not load QR image:", err);
    }

    try {
      const bottomLogoImg = await loadImageFromUrl(
        "/images/id-card/bottom_logo.png"
      );
      const bottomLogoWidth = 158;
      const bottomLogoHeight = 243;
      const bottomLogoX = width - bottomLogoWidth;
      const bottomLogoY = height - bottomLogoHeight;
      ctx.drawImage(
        bottomLogoImg,
        bottomLogoX,
        bottomLogoY,
        bottomLogoWidth,
        bottomLogoHeight
      );
    } catch (error) {
      console.warn("Could not load bottom logo:", error);
    }
  };

  const drawVisitingCardBack = async (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): Promise<void> => {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);

    const leftPartWidth = 0.6 * width;
    const rightPartStartX = leftPartWidth;
    try {
      const topLeftImg = await loadImageFromUrl("/images/id-card/top_left.png");

      const topLeftPaddingX = 0;
      const topLeftPaddingY = 0;
      const topLeftImgWidth = 68;
      const topLeftImgHeight = 43;

      ctx.drawImage(
        topLeftImg,
        topLeftPaddingX,
        topLeftPaddingY,
        topLeftImgWidth,
        topLeftImgHeight
      );
    } catch (err) {
      console.warn("Could not load top-left image:", err);
    }

    ctx.fillStyle = "#000000";
    ctx.font = "bold 22px Arial";
    ctx.textAlign = "left";
    ctx.fillText(`${user.first_name} ${user.last_name}`, 30, 80);

    ctx.textAlign = "left";
    ctx.font = "14px Arial";
    const bottomY = height - 30;
    ctx.fillText(user.mobile || "", 30, bottomY - 20);
    ctx.fillText(user.email || "", 30, bottomY);

    try {
      const logoImg = await loadImageFromUrl("/images/id-card/kazam_full.png");
      const logoWidth = 72;
      const logoHeight = 20;
      const logoRightPadding = 15;
      const logoTopPadding = 20;

      ctx.drawImage(
        logoImg,
        width - logoRightPadding - logoWidth,
        logoTopPadding,
        logoWidth,
        logoHeight
      );
    } catch (err) {
      console.warn("Could not load Kazam logo:", err);
    }
    try {
      const verticalContentX = rightPartStartX + 10;
      let currentY = 70;

      ctx.fillStyle = "#5b5c5c";
      ctx.font = "12px Arial";
      ctx.textAlign = "left";

      wrapText(
        ctx,
        "1st floor, Mp Krishna Mansion, 1st Cross Rd, KHB Colony, 5th Block, Koramangala, Bengaluru, Karnataka 560095",
        verticalContentX,
        currentY,
        width * 0.35,
        16
      );

      const addressLines = Math.ceil(
        ctx.measureText(
          "1st floor, Mp Krishna Mansion, 1st Cross Rd, KHB Colony, 5th Block, Koramangala, Bengaluru, Karnataka 560095"
        ).width /
          (width * 0.35)
      );
      currentY += addressLines * 16 + 10;

      const qrImg = await loadImageFromUrl("/images/id-card/url2.png");
      const qrSize = 59;
      ctx.drawImage(qrImg, verticalContentX, currentY, qrSize, qrSize);

      ctx.fillStyle = "#5b5c5c";
      ctx.font = "12px Arial";
      ctx.fillText("www.kazam.in", verticalContentX, currentY + qrSize + 18);

      ctx.fillText("Kazam EV app", verticalContentX, currentY + qrSize + 50);

      const googleIcon = await loadImageFromUrl("/images/id-card/google.png");
      const appleIcon = await loadImageFromUrl("/images/id-card/apple.png");

      const iconWidth = 45;
      const iconHeight = 13;
      const gap = 7;
      const rightPadding = 15;
      const totalIconsWidth = iconWidth * 2 + gap;

      const firstIconX = width - rightPadding - totalIconsWidth;
      const iconsY = currentY + qrSize + 35;

      ctx.drawImage(googleIcon, firstIconX, iconsY, iconWidth, iconHeight);

      ctx.drawImage(
        appleIcon,
        firstIconX + iconWidth + gap,
        iconsY,
        iconWidth,
        iconHeight
      );
    } catch (err) {
      console.warn("Could not load QR or icons:", err);
    }
  };

  const loadImageFromUrl = (url: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));

      if (url.startsWith("/")) {
        img.src = `${window.location.origin}${url}`;
      } else {
        img.src = url;
      }
    });
  };

  const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number
  ): void => {
    const words = text.split(" ");
    let line = "";
    let currentY = y;

    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + " ";
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > maxWidth && n > 0) {
        ctx.fillText(line, x, currentY);
        line = words[n] + " ";
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, currentY);
  };

  return (
    <div className="lg:flex gap-6">
      {/* cards front */}
      <div className="id-card-front-wrapper">
        <div className="id-front-wrapper  bg-white relative">
          <div className="">
            <img
              src="/images/id-card/kazamLogo.png"
              alt=""
              className="kazam-logo-id"
            />
          </div>
          <div className="id-text-heading text-xl">
            Single platform for EV charging
          </div>

          <div className="id-code-web">
            <img src="/images/id-card/web_url.png" alt="" />
          </div>
          <div className="kazam-url text-xs">www.kazam.in</div>

          <div className="absolute bottom-0 right-0">
            <img src="/images/id-card/bottom_logo.png" alt="" className="" />
          </div>
        </div>
      </div>
      {/* Cards back */}
      <div className="mt-12 lg:mt-0 id-card-back-wrapper">
        <div className="id-back-wrapper bg-white relative" id="back_card">
          <div className="absolute top-0 left-0">
            <img src="/images/id-card/top_left.png" alt="" />
          </div>
          <div className="name-id text-xl font-bold focus:outline-none">
            {user.first_name + " " + user.last_name}
          </div>
          <div
            className="des-id text-lg "
            style={{
              color: "#5B5C5C",
            }}
          >
            {user.emp_profile?.designation}
          </div>
          <div className="kazam-full">
            <img
              src="/images/id-card/kazam_full.png"
              alt=""
              className="kazam-full-img"
            />
          </div>
          <div className="id-address text-xs">
            1st floor, Mp Krishna Mansion
            <br />, 1st Cross Rd, KHB Colony, 5th Block,
            <br /> Koramangala, Bengaluru,
            <br /> Karnataka 560095
          </div>
          <div className="id-qr2">
            <img src="/images/id-card/url2.png" alt="" className="id-qr2-img" />
          </div>
          <div className="text-xs kazam-url2">www.kazam.in</div>
          <div className="phone-id">{user.mobile}</div>
          <div className="email-id">{user.email}</div>
          <div className="text-xs kazam-app-text">Kazam EV app</div>
          <div className="google-id">
            <img
              src="/images/id-card/google.png"
              alt=""
              className="google-id-img"
            />
          </div>
          <div className="apple-id">
            <img
              src="/images/id-card/apple.png"
              alt=""
              className="apple-id-img"
            />
          </div>
        </div>
      </div>
      <div className="mt-12 lg:mt-0">
        {loading ? (
          <HrmLoader className="w-6 h-6" />
        ) : (
          <button
            className="py-3 px-12 rounded-md text-white bg-hrmBlue"
            onClick={generateNewCanvasIdCard}
          >
            Download
          </button>
        )}
      </div>
    </div>
  );
};

export default VisitingCard;
